import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const PHOTO_STORAGE_KEY = 'securecoop_profile_photo';

function getInitials(name) {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

export default function ProfileModal({ isOpen, onClose }) {
  const { user, updateUser } = useAuth();
  const { t } = useLanguage();
  const [name, setName] = useState(user?.name || '');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoChanged, setPhotoChanged] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  const initialPhoto = user?.profilePhoto || localStorage.getItem(PHOTO_STORAGE_KEY) || '';

  useEffect(() => {
    if (isOpen) {
      setName(user?.name || '');
      const photo = user?.profilePhoto || localStorage.getItem(PHOTO_STORAGE_KEY) || '';
      setPhotoUrl(photo);
      setPhotoChanged(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setMessage({ type: '', text: '' });
    }
  }, [isOpen, user?.name, user?.profilePhoto]);

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoUrl(reader.result);
      setPhotoChanged(true);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setPhotoUrl('');
    setPhotoChanged(true);
  };

  const handleSave = async () => {
    setMessage({ type: '', text: '' });
    setSaving(true);

    const passwordFieldsFilled = currentPassword || newPassword || confirmPassword;
    if (passwordFieldsFilled) {
      if (!currentPassword) {
        setMessage({ type: 'error', text: t.errorCurrentPassword });
        setSaving(false);
        return;
      }
      if (newPassword !== confirmPassword) {
        setMessage({ type: 'error', text: t.errorPasswordsMismatch });
        setSaving(false);
        return;
      }
      if (newPassword.length < 6) {
        setMessage({ type: 'error', text: t.errorPasswordMin });
        setSaving(false);
        return;
      }
    }

    try {
      let savedPhoto = false;
      let savedName = false;

      if (photoChanged) {
        const newPhoto = photoUrl || '';
        localStorage.setItem(PHOTO_STORAGE_KEY, newPhoto);
        updateUser({ profilePhoto: newPhoto });
        savedPhoto = true;
      }

      if (name && name !== user?.name) {
        updateUser({ name });
        savedName = true;
      }

      if (savedPhoto && !savedName && !passwordFieldsFilled) {
        setMessage({ type: 'success', text: t.successPhotoUpdated });
      } else if (passwordFieldsFilled) {
        setMessage({
          type: 'success',
          text: t.successProfilePasswordNote,
        });
      } else {
        setMessage({ type: 'success', text: t.successProfileUpdated });
      }

      setPhotoChanged(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || t.errorSaving });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const displayPhoto = photoUrl || initialPhoto;

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-soft-lg w-full max-w-md overflow-hidden animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-600">
          <h2 className="text-lg font-semibold text-title">{t.profile}</h2>
          <p className="text-sm text-muted mt-0.5">{t.manageInfo}</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Photo */}
          <div>
            <h3 className="text-sm font-semibold text-title mb-3">{t.profilePhoto}</h3>
            <div className="flex flex-col items-center gap-3">
              <div
                className="w-24 h-24 rounded-full bg-cyan-500/15 flex items-center justify-center overflow-hidden border-2 border-slate-300 dark:border-slate-600 hover:border-cyan-500/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {displayPhoto ? (
                  <img src={displayPhoto} alt={t.photoPreview} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-cyan-400">{getInitials(user?.name)}</span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-cyan-400 hover:text-cyan-300 font-medium"
                >
                  {t.uploadNewPhoto}
                </button>
                {displayPhoto && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="text-sm text-slate-500 hover:text-red-600 font-medium"
                  >
                    {t.removePhoto}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Name & Email */}
          <div>
            <label className="block text-sm font-medium text-muted mb-1">{t.name}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full input-base"
              placeholder={t.yourName}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-muted mb-1">{t.email}</label>
            <input
              type="email"
              value={user?.email || ''}
              readOnly
              className="w-full input-base cursor-not-allowed opacity-80"
            />
            <p className="text-xs text-slate-400 mt-1">{t.emailReadonly}</p>
          </div>

          {/* Account Security - Password */}
          <div className="pt-6 border-t border-slate-200 dark:border-slate-600">
            <h3 className="text-sm font-semibold text-title mb-3">{t.accountSecurity}</h3>
            <p className="text-xs text-slate-400 mb-3">{t.changePassword}</p>
            <div className="space-y-3">
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t.currentPassword}
                className="w-full input-base"
              />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t.newPassword}
                className="w-full input-base"
              />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t.confirmPassword}
                className="w-full input-base"
              />
            </div>
          </div>

          {message.text && (
            <div
              className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${
                message.type === 'success'
                  ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                  : message.type === 'error'
                  ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                  : 'bg-slate-700/40 text-slate-400'
              }`}
            >
              {message.type === 'success' && (
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
              {message.text}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 px-4 rounded-lg border border-slate-300 dark:border-slate-600 text-title font-medium hover:bg-slate-200 dark:hover:bg-slate-700/40 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 px-4 rounded-lg bg-cyan-500 text-white font-medium hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t.saving : t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
